<template>
  <div :class="['activity-container', {'is-91xj': HNewGameUIConfig.newGameUIType === 3}]">
    <!-- Top tag filter row (teleported to TabReward sticky slot so it pins with the tabs) -->
    <teleport to="#activity-tag-row-slot" v-if="teleportReady">
      <div ref="tagRowRef" :class="['tag-row', {'is-91xj': HNewGameUIConfig.newGameUIType === 3}]" @touchstart.stop @touchmove.stop>
        <div
            v-for="(tag, index) in tagList"
            :key="tag.group_tag"
            :ref="el => setTagItemRef(el, index)"
            :class="['tag-item', { 'tag-active': activeTagIndex === index }]"
            @click="onTagClick(index)"
        >
          {{ tag.group_name }}
        </div>
      </div>
    </teleport>

    <!-- Empty state -->
    <div v-if="filteredList.length === 0" class="activity-empty">暂无活动</div>

    <!-- Left-right split: activity list + detail -->
    <div v-else class="activity-body">
      <!-- Left: activity list -->
      <div class="activity-left" :style="{height: rightHeight + 'px'}" @touchstart.stop @touchmove.stop>
        <div
            v-for="(item, index) in filteredList"
            :key="item.id"
            :class="['activity-item', { 'activity-item-active': activeItemIndex === index }]"
            :style="getItemStyle(index)"
            @click="changeSwiperPage(index)"
        >
          <div class="activity-item-title">{{ truncate(item.title, 6, 5) }}</div>
          <div class="activity-item-desc">{{ truncate(item.subtitle, 7) }}</div>
          <span v-if="item.can_claim" class="activity-item-dot"></span>
        </div>
      </div>

      <!-- Right: detail via HSwiperLR -->
      <div class="activity-right" :style="{height: rightHeight + 'px'}">
        <HSwiperLR ref="swiperLrRef" :height="rightHeight" :data="filteredList" :onSwipeChange="onSwipeChange">
          <template #default="{ pageIndex }">
            <ActivityDetail :activity-id="filteredList[pageIndex]?.id" :detail="detailCache[filteredList[pageIndex]?.id]" :on-claim="onClaim" @refresh="onRefreshDetail(filteredList[pageIndex]?.id)"/>
          </template>
        </HSwiperLR>
        <!-- 悬浮：当前活动可领取数 > 1 时显示 -->
        <div v-if="currentClaimableCount > 1" class="activity-claim-all">
          <button class="activity-claim-all-btn" @click="onClaim(null)">一键领取（{{ currentClaimableCount }}）</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, onMounted, onActivated, nextTick} from 'vue';
import {showToast} from "vant";
import {useHHttp} from "@/hooks/useHHttp.js";
import {useHComApi} from "@/hooks/useHComApi.js";
import {hcf} from "@/components/utils/HComFunctions.js";
import HSwiperLR from "@/components/utils/HSwiperLR.vue";
import ActivityDetail from "@/components/pages/Activity/ActivityDetail.vue";
import HNewGameUIConfig from "@/components/utils/HNewGameUIConfig.js";

const http = useHHttp();
const {fetchRedDot} = useHComApi();

const tagList = ref([{group_tag: '', group_name: '综合'}]);
const activityList = ref([]);
const detailCache = ref({});
const activeTagIndex = ref(0);
const activeItemIndex = ref(0);
const swiperLrRef = ref();
const tagRowRef = ref();
const tagItemRefs = ref([]);
const rightHeight = ref(window.innerHeight - 150);
const teleportReady = ref(false);

function setTagItemRef(el, index) {
  if (el) tagItemRefs.value[index] = el;
}

function scrollTagToCenter(index) {
  nextTick(() => {
    const row = tagRowRef.value;
    const item = tagItemRefs.value[index];
    if (!row || !item) return;
    const target = item.offsetLeft + item.offsetWidth / 2 - row.clientWidth / 2;
    const max = row.scrollWidth - row.clientWidth;
    row.scrollTo({left: Math.max(0, Math.min(target, max)), behavior: 'smooth'});
  });
}

const filteredList = computed(() => {
  const tag = tagList.value[activeTagIndex.value];
  if (!tag || tag.group_tag === '' || tag.group_tag === 'all') return activityList.value;
  return activityList.value.filter(a => a.group_tag === tag.group_tag);
});

const currentClaimableCount = computed(() => {
  const cur = filteredList.value[activeItemIndex.value];
  if (!cur) return 0;
  const det = detailCache.value[cur.id];
  if (det?.activity?.act_type === 2) return 0;
  return (det?.awards ?? []).filter(a => a.can_claim).length;
});

function onTagClick(index) {
  activeTagIndex.value = index;
  activeItemIndex.value = 0;
  swiperLrRef.value?.changeSwiperPage(0);
  loadDetailFor(0);
  scrollTagToCenter(index);
}

function changeSwiperPage(index) {
  activeItemIndex.value = index;
  swiperLrRef.value?.changeSwiperPage(index);
  loadDetailFor(index);
}

function onSwipeChange(index) {
  activeItemIndex.value = index;
  loadDetailFor(index);
}

function loadDetailFor(index) {
  const it = filteredList.value[index];
  if (!it) return;
  loadDetail(it.id);
}

function truncate(text, max, keep = max) {
  if (!text) return '';
  return text.length > max ? text.slice(0, keep) + '...' : text;
}

function getItemStyle(index) {
  const isActive = activeItemIndex.value === index;
  const is91xj = HNewGameUIConfig.newGameUIType === 3;
  if (is91xj) {
    return {
      backgroundColor: isActive ? hcf.getMainColor() : '#2a2a2a',
      borderColor: isActive ? hcf.getMainColor() : '#3a3a3a',
    };
  }
  return {
    backgroundColor: isActive ? hcf.getMainColor() : '#f5f5f5',
    borderColor: isActive ? hcf.getMainColor() : '#d2d9f9',
  };
}

function fetchActivities() {
  http.request("/qp/app/activity/list", (data) => {
    if (!data) return;
    const groups = [...(data.groups || [])].sort((a, b) => (b.seq ?? 0) - (a.seq ?? 0));
    tagList.value = groups.length ? groups : [{group_tag: '', group_name: '综合'}];
    activityList.value = data.activities || [];
    if (filteredList.value[0]) loadDetail(filteredList.value[0].id);
  }, {params: {group_tag: ''}, isShowLoading: false});
}

function loadDetail(activityId) {
  if (!activityId) return;
  http.request("/qp/app/activity/detail", (data) => {
    if (data) detailCache.value = {...detailCache.value, [activityId]: data};
  }, {params: {activity_id: activityId}, isShowLoading: false});
}

function onRefreshDetail(activityId) {
  if (!activityId) return;
  delete detailCache.value[activityId];
  loadDetail(activityId);
  fetchActivities();
  fetchRedDot();
}

function onClaim(award) {
  const it = filteredList.value[activeItemIndex.value];
  if (!it) return;
  const detail = detailCache.value[it.id];
  const params = {activity_id: it.id};
  if (detail?.activity?.award_mode === 2 && award?.id) {
    params.award_id = award.id;
  }
  http.request("/qp/app/activity/claim", (data) => {
    const amount = (data?.award_amount ?? 0) / 100;
    showToast(`领取成功 +${amount.toFixed(2)} 元`);
    delete detailCache.value[it.id];
    loadDetail(it.id);
    fetchActivities();
    fetchRedDot();
  }, {params});
}

let firstActivation = true;
onMounted(() => {
  fetchActivities();
  nextTick(() => {
    teleportReady.value = !!document.getElementById('activity-tag-row-slot');
  });
});
onActivated(() => {
  if (firstActivation) {
    firstActivation = false;
    return;
  }
  fetchRedDot();
  const it = filteredList.value[activeItemIndex.value];
  if (it) loadDetail(it.id);
});
</script>

<style scoped>
.activity-container {
  display: flex;
  flex-direction: column;
}

.tag-row {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  padding: 10px 10px 8px;
  gap: 10px;
  flex-shrink: 0;
  -webkit-overflow-scrolling: touch;
  background: var(--ct-main-bg-color, #fff);
}

.tag-item {
  flex-shrink: 0;
  padding: 2px 10px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

.tag-active {
  color: var(--ct-main-color, #3b6ce8);
  border-color: var(--ct-main-color, #3b6ce8);
  font-weight: bold;
}

.activity-empty {
  text-align: center;
  color: #999;
  font-size: 13px;
  padding: 60px 0;
}

.activity-body {
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: hidden;
  padding: 0 5px;
}

.activity-right {
  flex: 1;
  position: relative;
  min-width: 0;
}

.activity-claim-all {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  z-index: 10;
  text-align: center;
  pointer-events: none;
}

.activity-claim-all-btn {
  pointer-events: auto;
  background: var(--ct-main-color);
  color: #fff;
  border: none;
  padding: 10px 40px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
}

.activity-left {
  width: 80px;
  flex-shrink: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.activity-item {
  position: relative;
  text-align: center;
  padding: 2px 0;
  margin-top: 10px;
  border-radius: 10px 0 0 10px;
  border-top: 1px solid #d2d9f9;
  border-left: 1px solid #d2d9f9;
  border-bottom: 1px solid #d2d9f9;
  cursor: pointer;
  transition: all 0.2s;
  box-sizing: border-box;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.activity-item-title {
  font-size: 11px;
  color: #555;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-item-desc {
  font-size: 9px;
  color: #888;
  overflow: hidden;
  white-space: nowrap;
}

.activity-item-active .activity-item-title {
  color: #fff;
  font-weight: bold;
}

.activity-item-active .activity-item-desc {
  color: rgba(255, 255, 255, 0.75);
}

.activity-item-dot {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff4d4f;
}

/* ===== 91xj 暗色覆盖 ===== */
.is-91xj .activity-empty {
  color: #888;
}

.tag-row.is-91xj .tag-item {
  color: #bbb;
  border-color: #444;
}

.tag-row.is-91xj .tag-item.tag-active {
  color: var(--ct-main-color);
  border-color: var(--ct-main-color);
}

.is-91xj .activity-item-title {
  color: #ddd;
}

.is-91xj .activity-item-desc {
  color: #888;
}

.is-91xj .activity-item-active .activity-item-title {
  color: #fff;
}

.is-91xj .activity-item-active .activity-item-desc {
  color: rgba(255, 255, 255, 0.75);
}
</style>
